import { AuthShell, authErrorCopy, LoginForm } from '@skylabs-digital/backoffice-kit';
import { useAccessDecision, useLoginFlow } from '@skylabs-digital/react-identity-access';
import { Navigate } from 'react-router-dom';

/**
 * El login del backoffice, sobre `useLoginFlow` de RIA y el `AuthShell` /
 * `LoginForm` de `@skylabs-digital/backoffice-kit` (el mismo markup que
 * idachu).
 *
 * ## Que reemplaza
 *
 * El `<LoginForm>` de RIA 3, que ademas de la logica traia su propio markup y
 * se le pasaba una hoja de estilos armada leyendo variables CSS con
 * `getComputedStyle`. Eso se fue entero: RIA es headless para este flujo —
 * el hook maneja la maquina de estados y el error TIPADO. El formulario, el
 * diseño y las palabras los pone el kit (`AuthShell`/`LoginForm`), no esta
 * pagina: asi cada backoffice comparte una sola implementacion de login en
 * vez de mantener su propio markup Tailwind en paralelo.
 *
 * ## Por que la redireccion sale de `useAccessDecision` y no de `GuestZone`
 *
 * `GuestZone` es `auth: 'forbidden'`: rebota a cualquiera que TENGA sesion. La
 * guarda de adentro (`ProtectedRoute` → `PlatformAdminZone`) exige algo mas
 * estrecho, el contexto `(platform, admin)`. Con los dos criterios distintos,
 * una sesion autenticada que NO es de plataforma quedaria rebotando entre
 * `/login` y `/` para siempre: la zona de adentro la manda al login y el login
 * la manda adentro.
 *
 * Hoy no se puede llegar a esa sesion —el BFF de esta superficie solo emite
 * `(platform, admin)`— pero eso es una propiedad del SERVIDOR, no algo que el
 * cliente verifique. Pedir el MISMO preset por nombre en los dos lados hace
 * que la coincidencia sea estructural: `allowed` aca es, literalmente, la
 * misma decision que deja entrar alla.
 *
 * ## Lo que el navegador manda
 *
 * SOLO `{ identifier, password }`, a `/api/bo/auth/login`. El `app_id` y el
 * contexto `(platform, admin)` los pone el BFF desde su surface. El cliente NO
 * nombra su propio alcance: si pudiera, la superficie no acotaria nada.
 *
 * El campo se sigue llamando `username` en el DOM (`autoComplete="username"`)
 * porque es lo que los gestores de contraseñas esperan; lo que viaja es
 * `identifier`, que es como lo llama el contrato de idachu.
 */
export function LoginPage() {
  // EL MISMO requisito que `ProtectedRoute`, nombrado igual. `useAccessDecision`
  // ademas declara la demanda de sesion, asi que el bootstrap perezoso de RIA 5
  // se dispara aca sin pedirlo a mano.
  const access = useAccessDecision({ preset: 'platformAdmin' });
  const { submit, status, error } = useLoginFlow();

  if (access.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (access.status === 'allowed') {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthShell title="Notification Engine" subtitle="Sign in to your backoffice account">
      <LoginForm
        onSubmit={submit}
        submitting={status === 'submitting'}
        errorMessage={authErrorCopy(error)}
      />
    </AuthShell>
  );
}
